import { Component, Input, OnDestroy, Type, ViewChild } from '@angular/core';
import { CompanyDashboardComponent } from 'components/company/company-dashboard.component';
import { CompanyOrdersComponent } from 'components/company/company-orders.component';
import { CompanyProductsComponent } from 'components/company/company-products.component';
import { ViewCompanySalesflowComponent } from 'components/company/company-salesflow.component';
import { ListContactComponent } from 'components/company/contact/list-contact.component';
import { EditCompany } from 'components/company/edit-company.component';
import { CompanyInvoicesComponent } from 'components/company/invoice/company-invoices.component';
import { ListCompanyFilesComponent } from 'components/company/list-company-files.component';
import { CompanyHostingComponent } from 'components/hosting/company-hosting/company-hosting.component';
import { Shortcut, ShortcutPanel } from 'components/layout/shortcut-panel/shortcut-panel.component';
import { CreateSupportflowComponent } from 'components/task/taskflow/create-supportflow.component';
import { CreateTaskflowComponent } from 'components/task/taskflow/create-taskflow.component';
import { ListTaskflowComponent } from 'components/task/taskflow/list-taskflow.component';
import { CredentialType } from 'enums/CredentialType';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DataTracking } from 'services/data-tracking';
import { NavigationService, PageTitle, UrlParam } from 'services/navigation.service';
import { CompanyInformationComponent } from './company-information.component';
import { CompanyContactComponent } from './contact/company-contact.component';
import { IconType } from 'components/layout/icon/icon.component';
import { CompanyMicrosoft365Component } from 'components/hosting/microsoft365/company-microsoft365.component';
import { TooltipComponent } from 'components/shared/tooltip/tooltip.component';
import { ViewLegalCasesComponent } from 'components/accounting/debt-collection/view-legal-cases.component';
import { PushService } from 'services/push.service';
import { HubAction } from 'enums/HubAction';
import { Subscription } from 'rxjs';
import { CompanyStatusSummaryModel } from 'models/generated/GormGateway.Models.CompanyStatusSummary';

@Component({
	selector: 'viewcompany',
	templateUrl: './viewcompany.component.html',
	styleUrls: ['./viewcompany.component.scss'],
	providers: [CompanyDataProviderFactory, DataTracking],
	imports: [CompanyContactComponent, ShortcutPanel, Layout, CompanyInformationComponent, ListContactComponent, TranslatePipe, TooltipComponent]
})
export class ViewCompanyComponent implements OnDestroy {
	private dataProvider?: CompanyDataProvider;
	@ViewChild(ListContactComponent) contactList!: ListContactComponent;

	credentialType = CredentialType;

	@Input() company = new CompanyModel();
	hasActiveLegalCase = false;
	companyUpdateSubscription: Subscription;

	@PageTitle()
	get pageTitle() { return this.company.name; }

	private _companyId: string = '';
	@UrlParam
	@Input()
	set companyId(id: string) {
		if (id) {
			this._companyId = id;
			this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);
			this.dataProvider.preloadAllData();
			this.dataProvider.getCompany().then(company => { this.company = company; this.navigation.refreshTitle(); });
			this.dataProvider.getCompanyStatus().then(status => { this.updateCompanyStatus(status); });
		}
	}
	get companyId() {
		return this.company?.id ?? this._companyId;
	}
	updateCompanyStatus(status: CompanyStatusSummaryModel) {
		this.hasActiveLegalCase = status.hasActiveLegalCase;
	}
	shortcuts: Shortcut[];
	showExtendedCompanyInformation = false;

	constructor(private readonly dataProviderFactory: CompanyDataProviderFactory, private readonly navigation: NavigationService, private readonly pushService: PushService) {
		this.shortcuts = [
			this.getShortcut('shortcut.dashboard', CompanyDashboardComponent, 'house'),
			this.getShortcut('shortcut.sales', ViewCompanySalesflowComponent, 'handshake-simple'),
			this.getShortcut('shortcut.create-taskflow', CreateTaskflowComponent),
			this.getShortcut('shortcut.taskflows', ListTaskflowComponent, 'clipboard-list'),
			this.getShortcut('shortcut.create-supportflow', CreateSupportflowComponent, 'user-headset'),
			this.getShortcut('shortcut.company-products', CompanyProductsComponent, 'file-contract'),
			this.getShortcut('shortcut.company-hosting', null, 'globe').setChildren([
				this.getShortcut('shortcut.company-websites-and-domains', CompanyHostingComponent, 'globe-pointer'),
				this.getShortcut('shortcut.company-microsoft365', CompanyMicrosoft365Component, 'envelope'),
			]),
			this.getShortcut('shortcut.company-invoices', CompanyInvoicesComponent, 'coins'),
			this.getShortcut('shortcut.company-files', ListCompanyFilesComponent, 'folder-tree'),
			this.getShortcut('shortcut.company-orders', CompanyOrdersComponent, 'hand-holding-dollar'),
		];
		this.companyUpdateSubscription = pushService.onCompanyChanges.subscribe(event => {
			if (event.action == HubAction.Update && event.data.companyId == this.companyId && this.dataProvider)
				this.dataProvider.getCompanyStatus(true).then(status => { this.updateCompanyStatus(status); });
		});
	}
	ngOnDestroy(): void {
		this.companyUpdateSubscription.unsubscribe();
    }

	newContact() {
		this.contactList.editNewContact();
	}

	editVatNumber() {
		this.navigation.openPopup(EditCompany, 'company.edit-cvr.popup-title')
			.then(view => view.company = this.company);
	}

	private navigateToChildView(viewType: Type<{ companyId: string | undefined }>) {
		this.navigation.openRelativeView(this, 'company-related-views', viewType)
			.then(view => {
				view.companyId = this.companyId;
				this.showExtendedCompanyInformation = (viewType === CompanyDashboardComponent);
			});
	}

	private getShortcut(name: string, viewComponent: Type<{ companyId: string | undefined; }> | null, icon?: IconType) {
		const shortcut = new Shortcut();
		shortcut.name = name;
		shortcut.description = `${name}-title`;
		if (icon) shortcut.icon = icon;
		if (viewComponent) {
			shortcut.component = viewComponent;
			shortcut.action = () => { this.navigateToChildView(viewComponent); };
		}
		return shortcut;
	}

	public async viewLegalCases() {
		const view = await this.navigation.openPopup(ViewLegalCasesComponent);
		view.company = this.company;
	}
}
